import { create } from 'zustand';

/**
 * Details of a version-routing mismatch: a request routed to a pinned API
 * version slot came back 404, meaning that slot's build does not contain the
 * route. The React app and its API slot are meant to be deployed in lockstep,
 * so this is a deployment mismatch ("should never happen"), not a normal
 * feature boundary — hence a blocking alarm rather than graceful degradation.
 */
export interface VersionMismatchDetails {
  /** The connection's WebVersion (e.g. "2.0") that routed the request. */
  webVersion: string;
  /** The version slot host the request was actually sent to. */
  host: string;
  /** The API route that was missing on that slot. */
  route: string;
}

interface VersionMismatchState {
  mismatch: VersionMismatchDetails | null;
  setMismatch: (d: VersionMismatchDetails) => void;
  clearMismatch: () => void;
}

export const useVersionMismatchStore = create<VersionMismatchState>((set) => ({
  mismatch: null,
  setMismatch: (d) => set({ mismatch: d }),
  clearMismatch: () => set({ mismatch: null }),
}));

/**
 * Called from the axios response interceptor when a version-routed request
 * 404s. Fires the blocking dialog exactly once per session so repeated failing
 * calls (e.g. alert polling) don't stack dialogs.
 */
let versionMismatchTriggered = false;

export function reportVersionMismatch(details: VersionMismatchDetails) {
  if (versionMismatchTriggered) return;
  versionMismatchTriggered = true;
  useVersionMismatchStore.getState().setMismatch(details);
}

export function resetVersionMismatchFlag() {
  versionMismatchTriggered = false;
  useVersionMismatchStore.getState().clearMismatch();
}
